import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import ZIndex from '../definitions/ZIndex';
import MessagePropType from '../definitions/MessagePropType';
import AnimatedFadeContainer from './AnimatedFadeContainer';


const GROW_DURATION = 1200;
const FADE_DURATION = 300;


function GrowingDecimalTakeover({
    isVisible = true,

    top = '50%',
    left = '50%',
    onAnimationEnd,

    completedMessage,
    messageDuration = 4000,

    ...otherProps
}) {
    const [ runAnimation, setRunAnimation ] = useState(false);
    const [ isAnimationComplete, setIsAnimationComplete ] = useState(false);
    const isMounted = useRef(true);

    const holdDuration = messageDuration;

    // It's possible the component gets unmounted before the timeout completes
    // Ensures we don't try to update the state when the component is not mounted
    useEffect(() => {
        return () => isMounted.current = false;
    }, []);

    // Begins the CSSTransition for the appropriate duration
    useEffect(() => {
        setRunAnimation(isVisible);
        const timer = isVisible && setTimeout(
            () => setRunAnimation(false),
            completedMessage ? holdDuration : GROW_DURATION,
        );

        return () => timer && clearTimeout(timer);
    }, [ isVisible ]);

    function handleAnimationComplete() {
        setIsAnimationComplete(true);

        setTimeout(() => {
            if (isMounted.current) {
                setIsAnimationComplete(false);
            }
        }, holdDuration - GROW_DURATION);
    }

    return (
        <GrowingDecimalTakeover.Container
            top={top}
            left={left}
            shouldFadeOut={!completedMessage}
            {...otherProps}
        >
            <CSSTransition
                in={runAnimation}
                timeout={{
                    enter: GROW_DURATION,
                    exit: FADE_DURATION,
                }}
                classNames="grow"
                mountOnEnter
                unmountOnExit

                onEntered={completedMessage ? handleAnimationComplete : null}
                onExited={() => onAnimationEnd && onAnimationEnd()}
            >
                <div className="growingDecimal" />
            </CSSTransition>

            <div
                className="completed"
                data-active={isAnimationComplete}
            >
                <AnimatedFadeContainer
                    isVisible={isAnimationComplete}
                    withColour={false}
                >
                    <h2 className={classNames('rh-title-2xl', 'completedMessage', 'rh-my-1_5')}>
                        {completedMessage}
                    </h2>
                </AnimatedFadeContainer>
            </div>

        </GrowingDecimalTakeover.Container>
    );
}

GrowingDecimalTakeover.propTypes = {
    isVisible: PropTypes.bool,
    top: PropTypes.string,
    left: PropTypes.string,
    onAnimationEnd: PropTypes.func,

    completedMessage: MessagePropType,
    messageDuration: PropTypes.number,
};

const GROW = keyframes`
    from {
        transform: scale(1);
    }
    to {
        transform: scale(200);
        /* We need to add this property so that
        the element does not become blurry while the
        scale transition. */
        backface-visibility: hidden;
    }
`;

GrowingDecimalTakeover.Container = styled.div`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    z-index: ${ZIndex.FULL_SCREEN};
    background-color: ${Colours.COCONUT};

    overflow: hidden;

    > .growingDecimal {
        position: absolute;
        top: ${props => props.top};
        left: ${props => props.left};
        width: ${Sizes.ICONS.S};
        height: ${Sizes.ICONS.S};
        border-radius: 100%;

        &.grow-enter {
            background-color: ${Colours.BLUEBERRY};
        }
        &.grow-enter-active {
            animation: ${GROW} ${GROW_DURATION}ms ease-out;
        }

        &.grow-exit {
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 0px;
            background-color: ${Colours.BLUEBERRY};
            opacity: 1;
        }

        ${props => props.shouldFadeOut &&`
            &.grow-exit-active {
                opacity: 0;

                transition: opacity ${FADE_DURATION}ms ease-in;
            }
        `}

    }

    > .completed {
        display: flex;
        align-items: center;
        justify-content: center;

        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: ${ZIndex.SPECIAL_CASES};

        background-color: ${Colours.BLUEBERRY};
        opacity: 1;

        &[data-active="false"] {
            opacity: 0;
            transition: opacity ${FADE_DURATION}ms ease-in;
        }

        .completedMessage {
            max-width: 600px;

            text-align: center;

            color: ${Colours.COCONUT};
        }
    }
`;

export default GrowingDecimalTakeover;
